/**
 * Per-boss music themes, consumed by systems/audio/MusicDirector.js. Like
 * data/sfx.js, no audio assets exist — every theme is a synthesis spec —
 * but the shape is deliberately asset-shaped (id -> data) so swapping a
 * theme to real stems later is a data change, not a scheduler rewrite.
 *
 * Grid: 16 steps of 8th notes = 2 bars of 4/4. `null` in a note lane is a
 * rest. `pads` holds one chord (array of frequencies) per bar, cycled.
 * `intensity` is the layer mix per escalation level — 0 while clearing the
 * wave stage that builds toward the boss, 1 once the boss itself is up,
 * 2 from phase 2 / desperation on — so a stage's whole arc runs on one
 * theme that thickens as the fight escalates, instead of tracks swapping.
 *
 * Frequencies are plain Hz (A2 = 110 etc.) so a theme reads as data, not
 * note-name parsing logic.
 */

export const DEFAULT_MUSIC_THEME = 'hollowSovereign';

export const MUSIC_THEMES = {
  // Solemn, stately lament in A minor — a hollow king on a dead throne.
  hollowSovereign: {
    bpm: 96,
    droneFreq: 55, // A1
    bassWave: 'triangle',
    melodyWave: 'square',
    bass: [110, null, 110, null, 98, null, 110, null, 87.31, null, 87.31, null, 98, null, 123.47, null],
    melody: [null, 220, null, null, 261.63, null, 246.94, null, null, 220, null, 174.61, null, null, 196, null],
    pads: [[220, 261.63, 329.63], [174.61, 220, 261.63]], // Am, F
    kick: [1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
    hat: [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0],
    intensity: [
      { bass: 0.9, melody: 0, pad: 0, perc: 0.5 },
      { bass: 1, melody: 0.9, pad: 0.5, perc: 0.8 },
      { bass: 1, melody: 1, pad: 0.9, perc: 1 },
    ],
  },
  // Eerie, weaving voices in E minor with chromatic neighbors — a choir
  // singing slightly wrong notes on purpose.
  rivenChoir: {
    bpm: 104,
    droneFreq: 82.41, // E2
    bassWave: 'triangle',
    melodyWave: 'triangle',
    bass: [82.41, null, 82.41, 87.31, null, 82.41, null, 77.78, 82.41, null, 98, null, 110, null, 116.54, null],
    melody: [329.63, null, 311.13, 329.63, null, 392, null, null, 369.99, null, 329.63, null, null, 246.94, null, null],
    pads: [[164.81, 196, 246.94], [155.56, 196, 233.08]], // Em, then an Eb-major smear a half-step off
    kick: [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0],
    hat: [0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1],
    intensity: [
      { bass: 0.9, melody: 0, pad: 0.3, perc: 0.4 },
      { bass: 1, melody: 0.9, pad: 0.6, perc: 0.8 },
      { bass: 1, melody: 1, pad: 1, perc: 1 },
    ],
  },
  // Heavy, slow embers in G minor — weight over speed.
  ashenThrone: {
    bpm: 84,
    droneFreq: 49, // G1
    bassWave: 'sawtooth',
    melodyWave: 'triangle',
    bass: [98, null, null, 98, null, null, 116.54, null, 87.31, null, null, 87.31, null, null, 130.81, null],
    melody: [196, null, null, 233.08, null, 196, null, null, 174.61, null, null, 196, null, null, null, null],
    pads: [[196, 233.08, 293.66], [174.61, 220, 261.63]], // Gm, F
    kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 1, 0],
    hat: [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 1],
    intensity: [
      { bass: 0.85, melody: 0, pad: 0.2, perc: 0.5 },
      { bass: 1, melody: 0.9, pad: 0.6, perc: 0.85 },
      { bass: 1, melody: 1, pad: 1, perc: 1 },
    ],
  },
  // --- Gauntlet interloper themes (2026-07 expansion, data/bosses.js) ---

  // A military escapement in D minor — the hat ticks EVERY eighth like a
  // metronome that cannot be argued with, the kick lands square on the
  // quarters, and the melody is clipped staccato cells that only ever move
  // on the grid. Nothing swings. That's the point.
  ironCadence: {
    bpm: 112,
    droneFreq: 36.71, // D1
    bassWave: 'square',
    melodyWave: 'square',
    bass: [73.42, null, 73.42, null, 110, null, 73.42, null, 73.42, null, 87.31, null, 110, null, 98, null],
    melody: [293.66, null, null, null, 293.66, null, 349.23, null, 293.66, null, null, null, 440, null, 349.23, null],
    pads: [[146.83, 174.61, 220], [146.83, 185, 220]], // Dm, then a D-major-third glint like a polished buckle
    kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
    hat: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
    intensity: [
      { bass: 0.9, melody: 0, pad: 0.2, perc: 0.6 },
      { bass: 1, melody: 0.9, pad: 0.5, perc: 0.9 },
      { bass: 1, melody: 1, pad: 0.9, perc: 1 },
    ],
  },
  // A lurching banquet in G# minor — the kick limps (never quite where the
  // last bar promised), the bass crawls chromatically upward like reaching
  // hands, and the melody keeps stuffing in one more note before the bar
  // ends. Greed with a time signature.
  famishedCourt: {
    bpm: 88,
    droneFreq: 51.91, // G#1
    bassWave: 'sawtooth',
    melodyWave: 'triangle',
    bass: [103.83, null, null, 110, null, 116.54, null, null, 103.83, null, 110, null, 123.47, 130.81, null, null],
    melody: [null, 207.65, null, 233.08, 246.94, null, 207.65, null, null, 277.18, 261.63, 246.94, null, 233.08, 207.65, null],
    pads: [[103.83, 123.47, 155.56], [110, 130.81, 164.81]], // G#m, then the whole table shoved up a half step
    kick: [1, 0, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 0, 1, 1],
    hat: [0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1],
    intensity: [
      { bass: 0.85, melody: 0, pad: 0.3, perc: 0.5 },
      { bass: 1, melody: 0.9, pad: 0.65, perc: 0.8 },
      { bass: 1, melody: 1, pad: 1, perc: 1 },
    ],
  },
  // An obsessive waltz smuggled into 4/4 — F minor with the kick striking
  // every dotted eighth (a three-feel turning inside the bar, the hemiola
  // of a dance that won't let go), while the melody circles the same three
  // notes over and over, always coming back to where you were.
  jealousRegent: {
    bpm: 120,
    droneFreq: 43.65, // F1
    bassWave: 'triangle',
    melodyWave: 'triangle',
    bass: [87.31, null, null, 130.81, null, null, 87.31, null, null, 103.83, null, null, 87.31, null, 130.81, null],
    melody: [349.23, null, 415.3, null, 349.23, null, 329.63, null, 349.23, null, 415.3, null, 466.16, null, 415.3, 349.23],
    pads: [[174.61, 207.65, 261.63], [155.56, 207.65, 246.94]], // Fm, then Db-major leaning in too close
    kick: [1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1],
    hat: [0, 1, 1, 0, 1, 1, 0, 1, 1, 0, 1, 1, 0, 1, 1, 0],
    intensity: [
      { bass: 0.9, melody: 0, pad: 0.35, perc: 0.45 },
      { bass: 1, melody: 0.9, pad: 0.65, perc: 0.8 },
      { bass: 1, melody: 1, pad: 1, perc: 1 },
    ],
  },

  // --- Challenge-exclusive boss themes (data/challengeBosses.js) ---

  // Rolling, swelling D minor — bass keeps circling like water that never
  // settles; the melody rises and falls in waves rather than resolving.
  maelstromVerge: {
    bpm: 100,
    droneFreq: 36.71, // D1
    bassWave: 'triangle',
    melodyWave: 'triangle',
    bass: [73.42, null, 87.31, 110, null, 87.31, 73.42, null, 98, null, 110, 130.81, null, 110, 98, null],
    melody: [null, null, 293.66, null, 349.23, null, 440, null, null, 392, null, 349.23, null, 329.63, null, null],
    pads: [[146.83, 174.61, 220], [116.54, 146.83, 174.61]], // Dm, Bb
    kick: [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0],
    hat: [0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1],
    intensity: [
      { bass: 0.9, melody: 0, pad: 0.3, perc: 0.5 },
      { bass: 1, melody: 0.9, pad: 0.6, perc: 0.8 },
      { bass: 1, melody: 1, pad: 1, perc: 1 },
    ],
  },
  // Near-motionless C minor at a staring tempo — long rests, a heartbeat
  // kick, one harmonic-minor leading tone creeping in like a blink that
  // never comes.
  theUnblinking: {
    bpm: 72,
    droneFreq: 32.7, // C1
    bassWave: 'triangle',
    melodyWave: 'square',
    bass: [65.41, null, null, null, null, null, 77.78, null, 65.41, null, null, null, 103.83, null, 98, null],
    melody: [null, null, 261.63, null, null, null, null, 311.13, null, null, null, 246.94, null, null, null, null],
    pads: [[130.81, 155.56, 196], [103.83, 130.81, 155.56]], // Cm, Ab
    kick: [1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
    hat: [0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0],
    intensity: [
      { bass: 0.85, melody: 0, pad: 0.35, perc: 0.4 },
      { bass: 1, melody: 0.85, pad: 0.7, perc: 0.7 },
      { bass: 1, melody: 1, pad: 1, perc: 1 },
    ],
  },
  // Urgent B minor built on a relentless tick — the hat runs like an
  // escapement; the melody counts down a descending line that keeps
  // getting yanked back up, never allowed to finish.
  chronophage: {
    bpm: 132,
    droneFreq: 30.87, // B0
    bassWave: 'sawtooth',
    melodyWave: 'square',
    bass: [61.74, null, 61.74, null, 92.5, null, 61.74, null, 98, null, 110, null, 123.47, null, 146.83, 110],
    melody: [369.99, null, null, 440, null, 493.88, null, null, null, 587.33, 554.37, null, 493.88, null, 440, null],
    pads: [[123.47, 146.83, 185], [98, 123.47, 146.83]], // Bm, G
    kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
    hat: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 0, 1],
    intensity: [
      { bass: 0.9, melody: 0, pad: 0.2, perc: 0.6 },
      { bass: 1, melody: 0.9, pad: 0.6, perc: 0.9 },
      { bass: 1, melody: 1, pad: 1, perc: 1 },
    ],
  },

  // Frantic and chromatic around F# minor, tritone leaning — the gauntlet's
  // finale should feel like the music itself is coming apart.
  unmadeKing: {
    bpm: 126,
    droneFreq: 46.25, // F#1
    bassWave: 'sawtooth',
    melodyWave: 'square',
    bass: [92.5, 92.5, null, 92.5, 92.5, null, 92.5, 98, 92.5, 92.5, null, 92.5, 130.81, null, 123.47, null],
    melody: [369.99, null, 349.23, null, 329.63, null, 311.13, null, 293.66, null, null, 277.18, null, 261.63, null, null],
    pads: [[185, 220, 277.18], [174.61, 207.65, 261.63]], // F#m, then the same shape a half-step down
    kick: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 0, 1],
    hat: [0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 1],
    intensity: [
      { bass: 0.9, melody: 0, pad: 0.2, perc: 0.6 },
      { bass: 1, melody: 0.95, pad: 0.6, perc: 0.9 },
      { bass: 1, melody: 1, pad: 1, perc: 1 },
    ],
  },

  // --- Act II: The Undersong (data/act2Bosses.js) ---
  // The Gloam: slow, near-lightless F minor. Sub-bass hum, long rests, a
  // single lonely high note that surfaces and sinks — a room with the lights
  // off. The mix stays sparse even at full intensity; the darkness is the
  // point, and the music refuses to fill it.
  gloam: {
    bpm: 76,
    droneFreq: 43.65, // F1
    bassWave: 'sine',
    melodyWave: 'triangle',
    bass: [87.31, null, null, null, 82.41, null, null, null, 65.41, null, null, null, 73.42, null, null, null],
    melody: [null, null, null, 174.61, null, null, null, null, null, null, 155.56, null, null, null, null, null],
    pads: [[87.31, 130.81, 174.61], [82.41, 123.47, 164.81]], // Fm, then a step down into the dark
    kick: [1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
    hat: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
    intensity: [
      { bass: 0.7, melody: 0, pad: 0.15, perc: 0.2 },
      { bass: 0.9, melody: 0.5, pad: 0.4, perc: 0.4 },
      { bass: 1, melody: 0.8, pad: 0.7, perc: 0.6 },
    ],
  },

  // The Antiphon: two voices a step apart, quarrelling. The melody alternates
  // between two close pitches and the pads stack a chord against the same
  // shape a half-step up, so the whole theme audibly beats against itself.
  antiphon: {
    bpm: 92,
    droneFreq: 49, // G1
    bassWave: 'sawtooth',
    melodyWave: 'square',
    bass: [98, null, 98, null, 103.83, null, 98, null, 92.5, null, 98, null, 103.83, null, 110, null],
    melody: [392, null, 415.30, null, 392, null, 415.30, null, 349.23, null, 369.99, null, 392, null, 415.30, null],
    pads: [[196, 233.08, 293.66], [207.65, 246.94, 311.13]], // a chord, then the same shape a half-step up
    kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
    hat: [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0],
    intensity: [
      { bass: 0.9, melody: 0, pad: 0.3, perc: 0.4 },
      { bass: 1, melody: 0.9, pad: 0.6, perc: 0.8 },
      { bass: 1, melody: 1, pad: 1, perc: 1 },
    ],
  },

  // The Great Hush: the quietest theme in the game, and it stays quiet even at
  // full intensity — long silences, a single sine note that surfaces once a
  // bar, almost no percussion. The fight is about silence; the music yields to
  // it. (A future pass can have MusicDirector drop layers live as hush spreads.)
  greatHush: {
    bpm: 88,
    droneFreq: 41.20, // E1
    bassWave: 'sine',
    melodyWave: 'sine',
    bass: [82.41, null, null, null, null, null, 77.78, null, 65.41, null, null, null, null, null, 73.42, null],
    melody: [null, null, null, null, null, 246.94, null, null, null, null, null, null, 220, null, null, null],
    pads: [[164.81, 196, 246.94], [146.83, 185, 220]], // Em, then a hollow drop
    kick: [1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    hat: [0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
    intensity: [
      { bass: 0.6, melody: 0, pad: 0.1, perc: 0.15 },
      { bass: 0.8, melody: 0.4, pad: 0.3, perc: 0.3 },
      { bass: 0.95, melody: 0.6, pad: 0.5, perc: 0.4 },
    ],
  },

  // The Ballast: heavy and slow in D minor, a bassline that keeps sinking a
  // step at a time — weight over speed. Everything descends, like the well.
  ballast: {
    bpm: 80,
    droneFreq: 36.71, // D1
    bassWave: 'sawtooth',
    melodyWave: 'triangle',
    bass: [73.42, null, 69.30, null, 65.41, null, 61.74, null, 58.27, null, 55, null, 49, null, 55, null],
    melody: [null, null, 220, null, null, null, 196, null, null, null, 174.61, null, null, null, null, null],
    pads: [[110, 146.83, 174.61], [98, 130.81, 164.81]], // heavy, descending
    kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
    hat: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0],
    intensity: [
      { bass: 0.9, melody: 0, pad: 0.3, perc: 0.4 },
      { bass: 1, melody: 0.6, pad: 0.6, perc: 0.7 },
      { bass: 1, melody: 0.9, pad: 0.9, perc: 0.9 },
    ],
  },

  // The Swell: a rising A-minor theme, bassline and melody both climbing —
  // the sound of pressure building to meet you. (A future pass can drive the
  // layer mix live off the swell meter, per the bible.)
  swell: {
    bpm: 100,
    droneFreq: 55, // A1
    bassWave: 'triangle',
    melodyWave: 'sawtooth',
    bass: [110, null, 110, 130.81, null, 146.83, null, 164.81, 110, null, 110, 130.81, null, 174.61, null, 196],
    melody: [null, 329.63, null, 349.23, null, 392, null, 440, null, 493.88, null, 523.25, null, 587.33, null, null],
    pads: [[220, 261.63, 329.63], [246.94, 293.66, 349.23]], // rising
    kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 1, 0],
    hat: [0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1],
    intensity: [
      { bass: 0.9, melody: 0.2, pad: 0.3, perc: 0.5 },
      { bass: 1, melody: 0.8, pad: 0.7, perc: 0.9 },
      { bass: 1, melody: 1, pad: 1, perc: 1 },
    ],
  },

  // The Undersong (finale): begins as near-silence at the wave stage and
  // assembles into the fullest, darkest theme in the game as the fight
  // escalates — F# minor, tritone-leaning, every layer eventually in.
  undersong: {
    bpm: 112,
    droneFreq: 46.25, // F#1
    bassWave: 'sawtooth',
    melodyWave: 'square',
    bass: [92.5, null, 92.5, 98, 92.5, null, 103.83, null, 77.78, null, 82.41, null, 92.5, null, 61.74, null],
    melody: [369.99, null, 415.30, null, 440, null, 415.30, 369.99, null, 349.23, null, 329.63, null, 311.13, null, null],
    pads: [[185, 220, 277.18], [174.61, 220, 261.63]],
    kick: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1],
    hat: [0, 1, 0, 1, 0, 1, 1, 1, 0, 1, 0, 1, 1, 1, 0, 1],
    intensity: [
      { bass: 0.6, melody: 0, pad: 0.1, perc: 0.2 }, // near-silence
      { bass: 1, melody: 0.9, pad: 0.7, perc: 0.9 },
      { bass: 1, melody: 1, pad: 1, perc: 1 }, // fullest in the game
    ],
  },
  // --- Act III: The Anthem (data/act3Bosses.js) ---------------------------
  // Deliberately the inverse of Act II's descent into sub-bass and silence:
  // this act trends bright, major, and increasingly crowded, until the finale
  // is the fullest arrangement in the game. It is your own tune getting away
  // from you, so every theme here is built on a small repeating figure that
  // grows more voices than it started with.

  // The Ostinato: a four-bar figure that never resolves and never stops. The
  // bass is one cell repeated verbatim across both bars; the melody is the same
  // three notes each time, entering a beat later in the second half — the
  // theme is literally itself, on a delay.
  ostinato: {
    bpm: 96,
    droneFreq: 55, // A1
    bassWave: 'triangle',
    melodyWave: 'square',
    bass: [110, null, 110, 130.81, 110, null, 110, 130.81, 110, null, 110, 130.81, 110, null, 110, 130.81],
    melody: [440, null, 392, null, 329.63, null, null, null, null, 440, null, 392, null, 329.63, null, null],
    pads: [[220, 261.63, 329.63], [220, 261.63, 329.63]], // the same chord, twice: it does not move
    kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
    hat: [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0],
    intensity: [
      { bass: 0.9, melody: 0, pad: 0.3, perc: 0.5 },
      { bass: 1, melody: 0.9, pad: 0.6, perc: 0.85 },
      { bass: 1, melody: 1, pad: 0.9, perc: 1 },
    ],
  },

  // The Understudy: the player-champion's shape in a wrong key. A confident,
  // ordinary heroic figure in C — and the pad underneath it is a semitone out
  // the whole time, so the thing playing your part is not quite you.
  understudy: {
    bpm: 104,
    droneFreq: 65.41, // C2
    bassWave: 'sawtooth',
    melodyWave: 'square',
    bass: [130.81, null, 130.81, null, 174.61, null, 130.81, null, 116.54, null, 116.54, null, 155.56, null, 174.61, null],
    melody: [523.25, null, 392, null, 440, null, 523.25, null, null, 466.16, null, 392, null, 349.23, null, null],
    pads: [[261.63, 329.63, 392], [277.18, 349.23, 415.30]], // C, then the same shape a half-step up
    kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 1, 0],
    hat: [0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1],
    intensity: [
      { bass: 0.9, melody: 0, pad: 0.3, perc: 0.5 },
      { bass: 1, melody: 0.95, pad: 0.65, perc: 0.85 },
      { bass: 1, melody: 1, pad: 1, perc: 1 },
    ],
  },

  // The Tithe: a slow chorale in a stone room, with a coin-drop percussion
  // line — the hat is the coin, landing on the offbeat like something dropped
  // into a dish. Nothing hurries; collectors don't.
  tithe: {
    bpm: 84,
    droneFreq: 49, // G1
    bassWave: 'sine',
    melodyWave: 'triangle',
    bass: [98, null, null, null, 87.31, null, null, null, 73.42, null, null, null, 82.41, null, null, null],
    melody: [null, null, 293.66, null, null, null, 246.94, null, null, null, 220, null, null, null, 246.94, null],
    pads: [[196, 246.94, 293.66], [174.61, 220, 261.63]], // G, F — plain, churchy, unarguable
    kick: [1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
    hat: [0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1],
    intensity: [
      { bass: 0.85, melody: 0, pad: 0.4, perc: 0.4 },
      { bass: 1, melody: 0.8, pad: 0.75, perc: 0.7 },
      { bass: 1, melody: 1, pad: 1, perc: 0.9 },
    ],
  },

  // The Manifold: a canon at the unison — the melody's second half is its first
  // half, entered a half-bar late, so the theme is chasing itself the way the
  // second body chases your hands.
  manifold: {
    bpm: 100,
    droneFreq: 61.74, // B1
    bassWave: 'triangle',
    melodyWave: 'triangle',
    bass: [123.47, null, 123.47, null, 92.5, null, 123.47, null, 123.47, null, 123.47, null, 98, null, 110, null],
    melody: [493.88, null, 587.33, null, 493.88, null, 392, null, null, 493.88, null, 587.33, null, 493.88, null, 392],
    pads: [[246.94, 293.66, 369.99], [246.94, 311.13, 369.99]], // Bm, then B — the same voice, differently
    kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
    hat: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
    intensity: [
      { bass: 0.9, melody: 0, pad: 0.35, perc: 0.5 },
      { bass: 1, melody: 0.9, pad: 0.7, perc: 0.85 },
      { bass: 1, melody: 1, pad: 1, perc: 1 },
    ],
  },

  // The Verse: unfinished on purpose. The base layer is nearly bare — one bass
  // note and a rest where a tune should be — and the intensity ladder is the
  // steepest in the game, so the theme audibly accumulates as the fight does.
  // (The bible's "layers added per player pick" wants a live MusicDirector
  // signal; the escalation ladder carries the same idea with what exists.)
  verse: {
    bpm: 92,
    droneFreq: 58.27, // Bb1
    bassWave: 'triangle',
    melodyWave: 'sawtooth',
    bass: [116.54, null, null, null, 116.54, null, 155.56, null, 103.83, null, null, null, 116.54, null, 138.59, null],
    melody: [null, 466.16, null, 415.30, null, 349.23, null, null, null, 466.16, null, 523.25, null, 466.16, null, null],
    pads: [[233.08, 293.66, 349.23], [207.65, 261.63, 311.13]], // Bb, Ab
    kick: [1, 0, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 0, 1, 0],
    hat: [0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 1, 0, 1],
    intensity: [
      { bass: 0.7, melody: 0, pad: 0.1, perc: 0.25 }, // barely written yet
      { bass: 1, melody: 0.7, pad: 0.6, perc: 0.8 },
      { bass: 1, melody: 1, pad: 1, perc: 1 },
    ],
  },

  // The Anthem (finale): the largest arrangement in the game, and the only
  // major-key one. Its melody is assembled out of the shapes the player has
  // already heard — the Sovereign's rising fourth, the Choir's chromatic
  // neighbour, the King's descending line, the Undersong's tritone lean — all
  // sung together, in D major, at full voice. Everything you started.
  anthem: {
    bpm: 108,
    droneFreq: 73.42, // D2
    bassWave: 'sawtooth',
    melodyWave: 'square',
    bass: [146.83, null, 146.83, 185, 220, null, 185, null, 146.83, null, 164.81, null, 185, null, 110, null],
    melody: [587.33, null, 739.99, null, 880, null, 739.99, 698.46, null, 659.25, null, 587.33, null, 493.88, null, 587.33],
    pads: [[293.66, 369.99, 440], [329.63, 415.30, 493.88]], // D, then E — the chord that will not stay put
    kick: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 0, 1],
    hat: [1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 1, 1],
    intensity: [
      { bass: 0.8, melody: 0.3, pad: 0.4, perc: 0.5 }, // even the wave stage is loud here
      { bass: 1, melody: 1, pad: 0.85, perc: 0.95 },
      { bass: 1, melody: 1, pad: 1, perc: 1 },
    ],
  },

  sirenreef: {
    bpm: 94, droneFreq: 49, bassWave: 'triangle', melodyWave: 'sine',
    bass: [98, null, 110, null, 123.47, null, 110, null, 98, null, 87.31, null, 98, null, 110, null],
    melody: [null, 392, null, 440, null, 523.25, null, 440, null, 392, null, 349.23, null, 392, null, null],
    pads: [[196, 233.08, 293.66], [174.61, 220, 261.63]], kick: [1, 0, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 0, 1, 0], hat: [0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0],
    intensity: [{ bass: 0.9, melody: 0, pad: 0.3, perc: 0.4 }, { bass: 1, melody: 0.9, pad: 0.7, perc: 0.8 }, { bass: 1, melody: 1, pad: 1, perc: 1 }],
  },
  palimpsest: {
    bpm: 108, droneFreq: 41.2, bassWave: 'sawtooth', melodyWave: 'square',
    bass: [82.41, null, 98, null, 82.41, null, 110, null, 77.78, null, 98, null, 82.41, null, 123.47, null],
    melody: [329.63, null, null, 392, null, 349.23, null, 440, 329.63, null, null, 392, null, 311.13, null, null],
    pads: [[164.81, 196, 246.94], [155.56, 185, 233.08]], kick: [1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1], hat: [0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1],
    intensity: [{ bass: 0.9, melody: 0, pad: 0.3, perc: 0.5 }, { bass: 1, melody: 0.9, pad: 0.7, perc: 0.9 }, { bass: 1, melody: 1, pad: 1, perc: 1 }],
  },
};
